import { EAlertVariants } from 'components/atoms/Alert';
import { usePermission } from 'hooks/usePermission';
import { useTranslations } from 'hooks/useTranslations';
import { useAlert } from 'models/alertContext';
import { ILicenceData, ILicencePrintout } from 'models/licence';
import { EPermission } from 'models/permissions';
import { useEffect, useState } from 'react';
import {
  cancelLicencePrintRequest,
  enqueueLicencePrint,
  getLicenceDetail,
  getLicencePrintAvailability,
  getLicencePrintoutsHistory,
  redeemLicenceKey,
} from 'requests/licence';
import { ActionTypes } from 'state/actions/alert';

export const useLicence = () => {
  const [licence, setLicence] = useState<ILicenceData | null>(null);

  const getLicenceData = async (): Promise<ILicenceData | null> => {
    const licenceData = await getLicenceDetail();
    if (licenceData) {
      setLicence(licenceData);
      return licenceData;
    }
    setLicence(null);
    return null;
  };

  const replaceLicenceHandler = async (key: string) => redeemLicenceKey(key);

  return {
    licence,
    getLicenceData,
    replaceLicenceHandler,
  };
};

export const usePrintLicence = (licenceId: string) => {
  const [canPrintLicence, setCanPrintLicence] = useState<boolean>(false);
  const [historyPrintouts, setHistoryPrintouts] = useState<ILicencePrintout[] | null>(null);
  const { setAlert, clearAlert } = useAlert();
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();
  const hasPrintPermission = !!hasPermissions(EPermission.PRINT_LICENCE);

  const handleGetLicencePrintAvailability = async () => {
    if (!licenceId || !hasPrintPermission) {
      setCanPrintLicence(false);
      return;
    }
    try {
      const res = await getLicencePrintAvailability(licenceId);
      setCanPrintLicence(res?.canBePrinted ?? false);
    } catch {
      setCanPrintLicence(false);
    }
  };

  const handleGetLicencePrintoutsHistory = async () => {
    if (!licenceId || !hasPrintPermission) return;
    try {
      const res = await getLicencePrintoutsHistory(licenceId);
      setHistoryPrintouts(res ?? []);
    } catch {
      setHistoryPrintouts([]);
    }
  };

  useEffect(() => {
    handleGetLicencePrintAvailability();
    handleGetLicencePrintoutsHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenceId]);

  const handleEnqueueLicencePrint = async (): Promise<boolean> => {
    clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);

    let success: boolean;
    let errorMessage = '';

    try {
      success = await enqueueLicencePrint(licenceId);
    } catch (e: any) {
      success = false;
      errorMessage =
        e?.response?.data?.message || e?.response?.data?.errors?.[0]?.message || '';
    }

    setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
      text: success
        ? t('printLicence.success.alert')
        : errorMessage || t('printLicence.error.alert'),
      variant: success ? EAlertVariants.success : EAlertVariants.error,
    });

    await handleGetLicencePrintAvailability();
    await handleGetLicencePrintoutsHistory();
    return success;
  };

  return {
    canPrintLicence,
    handleEnqueueLicencePrint,
    historyPrintouts,
  };
};
